import { useEffect, useRef, useState } from 'react';

const DEFAULT_LOAD_TIMEOUT_MS = 15000;

export function useRecaptchaV2(siteKey, options = {}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);
  const { onChange, onExpired } = options;

  const getToken = () => {
    const grecaptcha = window.grecaptcha;
    const widgetId = widgetIdRef.current;
    if (!grecaptcha?.getResponse || widgetId === null) return '';
    try {
      return grecaptcha.getResponse(widgetId);
    } catch (_) {
      return '';
    }
  };

  const reset = () => {
    const grecaptcha = window.grecaptcha;
    const widgetId = widgetIdRef.current;
    if (!grecaptcha?.reset || widgetId === null) return;
    try {
      grecaptcha.reset(widgetId);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    if (!siteKey) return undefined;
    if (widgetIdRef.current !== null) return undefined;

    let cancelled = false;
    const startedAt = Date.now();

    const tryRender = () => {
      if (cancelled) return;
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha?.render || !containerRef.current) {
        if (Date.now() - startedAt > DEFAULT_LOAD_TIMEOUT_MS) return;
        window.setTimeout(tryRender, 150);
        return;
      }

      try {
        const widgetId = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: typeof onChange === 'function' ? onChange : undefined,
          'expired-callback': typeof onExpired === 'function' ? onExpired : undefined,
        });
        if (cancelled) return;
        widgetIdRef.current = widgetId;
        setReady(true);
      } catch (_) {
        if (Date.now() - startedAt > DEFAULT_LOAD_TIMEOUT_MS) return;
        window.setTimeout(tryRender, 300);
      }
    };

    tryRender();
    return () => {
      cancelled = true;
    };
  }, [siteKey, onChange, onExpired]);

  return { containerRef, getToken, reset, ready };
}

